/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useMemo, useState } from 'react'
import ProfilePicture from 'components/elements/ProfilePicture'
import { Know, KnowStatus, KnowTranscript } from 'interfaces/Know'
import styled from 'styled-components'
import NewButton, { NewButtonLink } from 'components/elements/NewButton'
import { useRouter } from 'next/router'
import { trackEvent } from 'lib/tracking/analytics-service'
import { knowEditRoute } from 'lib/constants/routes'
import { Body1, Body2, ButtonText, Headline1 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import Stack from 'components/elements/Stack'
import { useSelector } from 'react-redux'
import { AppState } from 'redux/reducer'
import _useTranslation from 'lib/hooks/_useTranslation'
import KnowPreviewSection from 'components/feature/Know/view/content/KnowPreviewSection'
import { redirectToAppDownloadUrlAsync } from 'lib/features/app-links'
import { appDeepLinkKnow } from 'lib/features/app-deep-links'
import {
    AnalyticsEventKnowContentView,
    AnalyticsEventKnowConversionDialogActionButtonClick,
    AnalyticsEventKnowConversionMobileContent,
} from 'lib/constants/AnalyticsEventType'
import { ClientOnly } from 'components/elements/ClientOnly'
import { Flashcard } from 'interfaces/Flashcard'
import KnowFlashcardPreview from 'components/modules/Knows/KnowFlashcardPreview'
import { useToggleState } from 'lib/hooks/useToggleState'
import { FeedEventOptions, SearchOptions } from 'interfaces/TrackingSnippets'
import { parseLocale } from 'lib/constants/localization'
import { getMatchingLocalizationInfo } from 'lib/features/Know/know'
import { returnKnowerFollowerLabel } from 'lib/formatting/knows'
import { getCountryLanguageConfig } from 'lib/constants/countryLanguageConfig'
import KnowActions from '../SEO/KnowActions'
import BlurredMobileImage from '../SEO/BlurredMobileImage'
import WebappConversionDialog from '../webapp/WebappConversionDialog'
import KnowService from 'services/KnowService'
import { ApiError } from 'services/ApiService'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { KnowerLink } from 'lib/features/Know/knower'
import { KnowSeoInfo } from 'interfaces/KnowSeoInfo'
import Row from 'components/elements/Row'
import { PractiseSet } from 'interfaces/PractiseSet'
import PractiseSetQuizPreview from 'components/modules/Knows/PractiseSetQuizPreview'
import { KnowFormat } from 'interfaces/Know'

const HeaderSection = styled.div`
    width: 100%;
    display: flex;
    justify-content: space-between;
    position: relative;
    .report {
        position: absolute;
        right: 0;
        top: 45%;
    }
    @media (max-width: 1050px) {
        padding: 5px 15px;
        flex-direction: column;
    }
    @media (max-width: 1050px) {
        padding: 7.5px;
    }
`

const KnowerSection = styled.div<{ isDarkTheme?: boolean }>`
    display: grid;
    grid-template-columns: 1fr;
    grid-gap: 15px;
    align-items: center;
    .button {
        padding: 0px;
        font-size: 12px;
    }

    .top {
        font-size: 18px;
        font-weight: 700;
        line-height: 27px;
        letter-spacing: 0em;
        text-align: left;
        color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.headlineDark)};
        @media (min-width: 1051px) {
            display: none;
        }
    }
    @media (max-width: 1050px) {
        margin-bottom: 10px;
        grid-template-columns: 1fr auto;
        justify-content: center;
    }
`

const KnowerCredentials = styled.div`
    display: grid;
    row-gap: 3px;
    @media (max-width: 1050px) {
        display: none;
    }
`

const KnowTitle = styled(Headline1)`
    font-size: 25px !important;
    line-height: 36px !important;
    font-weight: 600 !important;
    @media (max-width: 1050px) {
        display: none;
    }
`

const KnowArea = styled.div<{ isDarkTheme?: boolean; removeBackground?: boolean }>`
    height: auto;
    background-color: ${({ isDarkTheme }) =>
        isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.violetBackground};
    margin: 0;
    display: flex;
    position: relative;
    justify-content: center;
    align-items: center;
    overflow-y: hidden;
    min-width: 400px;
    padding: ${({ removeBackground }) => (removeBackground ? '0px' : '10px')};
    border-radius: 32px;
    @media (max-width: 1050px) {
        min-width: 200px;
        overflow: hidden;
    }
`

const KnowerInformation = styled.div`
    display: flex;
    gap: 15px;
    align-items: center;
    flex-wrap: wrap;
    justify-content: space-between;
`

const ButtonWrapper = styled.div`
    div {
        padding: 0px;
        font-size: 12px;
    }
`

const WarningMessage = styled.div`
    display: grid;
    gap: 20px;
    .buttons {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 15px;
    }
    width: 100%;
    background-color: rgba(255, 195, 39, 0.2);
    border: 0.1rem solid rgb(255, 195, 39);
    border-left: 10px solid rgb(255, 195, 39);
    padding: 10px;
    border-radius: ${theme.borderRadius.normal};
`

interface Props {
    knowSeoInfo: KnowSeoInfo
    isOwner?: boolean
    onContentConversionBannerButtonClick(platform: 'android' | 'ios'): void
    referrerScreen?: string
    isMobile?: boolean
    downloadDeepLinkPath: string
    showPreview?: boolean
    transcript?: KnowTranscript | null
    flashcards?: Flashcard[] | null
    showWebappActions?: boolean
    feed?: FeedEventOptions
    search?: SearchOptions
    similarKnows: Know[] | null
    isDarkTheme?: boolean
}

const KnowSEOContent = ({
    knowSeoInfo,
    isOwner,
    onContentConversionBannerButtonClick,
    referrerScreen,
    isMobile,
    downloadDeepLinkPath,
    showPreview,
    transcript,
    flashcards,
    showWebappActions,
    feed,
    search,
    similarKnows,
    isDarkTheme,
}: Props) => {
    const [showConversionDialog, toggleConversionDialog] = useToggleState(false)
    const [reviewMessage, setReviewMessage] = useState<string | null>()
    const [showContent, setShowContent] = useState(false)
    const [practiseSet, setPractiseSet] = useState<PractiseSet | null>(null)
    const { locale, reload } = useRouter()
    const parsedLocale = parseLocale(locale)
    const authenticatedUser = useSelector((state: AppState) => state.user)
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('knows')
    const [conversionReferrerScreen, setConversionReferrerScreen] = useState<string | undefined>()
    const countryLanguageConfig = getCountryLanguageConfig(locale)
    const isLaunched = !!countryLanguageConfig?.isLaunched

    const { know } = knowSeoInfo

    const knowLocalization = useMemo(
        () => getMatchingLocalizationInfo(know.localizations, parsedLocale),
        [know.localizations, parsedLocale]
    )

    useEffect(() => {
        if (!isOwner) return

        KnowService.getReviewHistory(know.uuid)
            .then((history) => {
                if (history.length === 0) return
                setReviewMessage(history[0].message)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    if (error.type === ErrorTypes.NotFound) return

                    error.handleUnknownError(tCommon, 'know.getReviewHistory')
                } else {
                    throw error
                }
            })
    }, [know.uuid, tCommon, isOwner])

    useEffect(() => {
        if (authenticatedUser || !!showPreview) setTimeout(() => setShowContent(true), 0)
    }, [authenticatedUser, isMobile, showPreview])

    useEffect(() => {
        if (know.knowFormat !== KnowFormat.PracticeSet && authenticatedUser) return

        KnowService.getPractiseGameQuiz(know.uuid)
            .then((practiseSetData) => {
                // Filter to only include supported question types
                const filteredPractiseSet = {
                    ...practiseSetData,
                    flashcards: practiseSetData.flashcards.filter(
                        (question) => question.type === 'TRUE_FALSE' || question.type === 'MULTIPLE_CHOICE'
                    ),
                }
                setPractiseSet(filteredPractiseSet)
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'know.getPractiseGameQuiz')
                } else {
                    throw error
                }
            })
    }, [know.uuid, know.knowFormat, tCommon])

    const handleConversionDialogActionButtonClick = (platform: 'android' | 'ios', notRedirectToAppDownload?: boolean) => {
        trackEvent(AnalyticsEventKnowConversionDialogActionButtonClick, {
            know: know ?? undefined,
            useBeacon: true,
            referrerScreen: conversionReferrerScreen,
            feed,
        })
        if (!notRedirectToAppDownload) {
            const referrerScreen = `knowSeoPage${conversionReferrerScreen ? `-${conversionReferrerScreen}` : ''}`
            redirectToAppDownloadUrlAsync(platform, referrerScreen, appDeepLinkKnow(know.uuid))
        }
    }

    const onKnowActionClicked = (type: 'Like' | 'Comment' | 'Save' | 'Share') =>
        setConversionReferrerScreen(`knowConversion${type}`)

    const trackContentOpenEvent = () =>
        trackEvent(isMobile ? AnalyticsEventKnowConversionMobileContent : AnalyticsEventKnowContentView, {
            referrerScreen,
            know,
            feed,
            search,
        })

    const setToDraft = () => {
        if (!know.uuid) return

        KnowService.updateStatus(know.uuid, KnowStatus.Draft)
            .then(() => {
                reload()
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    error.handleUnknownError(tCommon, 'knows.updateStatus')
                } else {
                    throw error
                }
            })
    }

    const showEditButton = false //know.status !== KnowStatus.PermanentlyRejected && know.status !== KnowStatus.Public && !flashcards && isOwner
    const showRejectedWarningButton = showEditButton && know.status === KnowStatus.Rejected && reviewMessage
    const showPractiseSetQuiz = !!(know.knowFormat === KnowFormat.PracticeSet && practiseSet)

    const KnowPreview = () => (
        <KnowPreviewSection
            knowSeoInfo={knowSeoInfo}
            trackContentOpenEvent={trackContentOpenEvent}
            onContentConversionBannerButtonClick={onContentConversionBannerButtonClick}
            showFixedBanner={isLaunched}
            disableExperiments={showPreview}
            transcript={transcript}
            similarKnows={similarKnows}
            isDarkTheme={isDarkTheme}
        />
    )

    return (
        <div>
            <Stack gutter={15}>
                <KnowTitle color={isDarkTheme ? theme.colors.white : theme.colors.headlineDark}>
                    {knowLocalization?.title ?? knowSeoInfo.title}
                </KnowTitle>
                {showContent ? (
                    <KnowArea isDarkTheme={isDarkTheme} removeBackground={showPractiseSetQuiz}>
                        {flashcards ? (
                            <KnowFlashcardPreview know={know} flashcards={flashcards} isDarkTheme={isDarkTheme} />
                        ) : showPractiseSetQuiz ? (
                            <PractiseSetQuizPreview know={know} practiseSet={practiseSet} isDarkTheme={isDarkTheme} />
                        ) : (
                            <KnowPreview />
                        )}
                    </KnowArea>
                ) : (
                    <>
                        {isMobile ? (
                            <BlurredMobileImage
                                know={know}
                                referrerScreen={referrerScreen}
                                downloadDeepLinkPath={downloadDeepLinkPath}
                                transcript={transcript}
                            />
                        ) : (
                            <KnowArea isDarkTheme={isDarkTheme}>
                                {flashcards ? (
                                    <KnowFlashcardPreview know={know} flashcards={flashcards} isDarkTheme={isDarkTheme} />
                                ) : know.knowFormat === KnowFormat.PracticeSet && practiseSet ? (
                                    <PractiseSetQuizPreview
                                        know={know}
                                        practiseSet={practiseSet}
                                        isDarkTheme={isDarkTheme}
                                    />
                                ) : (
                                    <KnowPreview />
                                )}
                            </KnowArea>
                        )}
                    </>
                )}
                <HeaderSection data-nosnippet="true">
                    <div>
                        {isLaunched ? (
                            <KnowActions
                                isDarkTheme={isDarkTheme}
                                isMobile={!!isMobile}
                                knowSeoInfo={knowSeoInfo}
                                showPreview={!!showPreview}
                                removeDesktopCommentSection
                                showWebappActions={showWebappActions}
                                onActionClicked={onKnowActionClicked}
                                referrerScreen={conversionReferrerScreen}
                            />
                        ) : null}
                    </div>
                </HeaderSection>
                <KnowerSection isDarkTheme={isDarkTheme}>
                    <Body2 color={theme.colors.grayScaleSecondary} fontFamily="var(--font-inter)" fontWeight={400}>
                        {knowLocalization?.description ?? know.description ?? know.generatedDescription}
                    </Body2>

                    <KnowerInformation>
                        <Row>
                            <ProfilePicture user={know.knower.user} size={40} borderColor={theme.colors.outlineGray} />
                            <KnowerLink knower={know.knower} handleLinksWithinWebapp={showWebappActions}>
                                <KnowerCredentials>
                                    <Body1
                                        color={theme.colors.grayScaleSecondary}
                                        fontFamily="var(--font-inter)"
                                        fontWeight={500}
                                    >
                                        {know.knower.user.name ?? know.knower.user.username}
                                    </Body1>
                                    <Body2
                                        color={theme.colors.grayScaleSecondary}
                                        fontWeight={400}
                                        fontFamily="var(--font-inter)"
                                    >
                                        {returnKnowerFollowerLabel(know, t, locale)}
                                    </Body2>
                                </KnowerCredentials>
                            </KnowerLink>
                        </Row>

                        <ButtonWrapper>
                            {showEditButton ? (
                                <NewButtonLink
                                    href={knowEditRoute(know.uuid)}
                                    width={150}
                                    height={40}
                                    borderRadius={theme.borderRadius.huge}
                                    color={theme.colors.backgroundTransparent}
                                >
                                    <ButtonText color={theme.colors.grayScaleSecondary}>
                                        {t('knows/knowDetailsEditButton')}
                                    </ButtonText>
                                </NewButtonLink>
                            ) : null}
                        </ButtonWrapper>
                    </KnowerInformation>
                </KnowerSection>

                {/* <BadgeWrapper>
                    <GradeBadge grades={know.grades} isSmall />
                    {knowLocalization?.knowType ?? know.knowType ? (
                        <KnowTypeBadge knowType={knowLocalization?.knowType ?? know.knowType} isSmall />
                    ) : null}
                    {!(flashcards?.length && know.status === KnowStatus.Draft) ? (
                        <KnowStatusBadge status={know.status} isSmall />
                    ) : null}
                </BadgeWrapper> */}

                {showRejectedWarningButton ? (
                    <WarningMessage>
                        <Body2>{reviewMessage}</Body2>
                        <div className="buttons">
                            <NewButton
                                type="secondary"
                                color={theme.colors.deepSeaBlue}
                                borderRadius={theme.borderRadius.normal}
                                fullWidth
                                onClick={setToDraft}
                            >
                                <ButtonText color={theme.colors.deepSeaBlue}>
                                    {t('knows/knowDetailsMakePrivateButtonLabel')}
                                </ButtonText>
                            </NewButton>
                            <NewButtonLink
                                href={knowEditRoute(know.uuid)}
                                color={theme.colors.deepSeaBlue}
                                borderRadius={theme.borderRadius.normal}
                                fullWidth
                            >
                                <ButtonText color={theme.colors.white}>
                                    {t('knows/knowDetailsEditAndPublishButtonLabel')}
                                </ButtonText>
                            </NewButtonLink>
                        </div>
                    </WarningMessage>
                ) : null}
                <ClientOnly>
                    {isLaunched ? (
                        <KnowActions
                            isMobile={!!isMobile}
                            knowSeoInfo={knowSeoInfo}
                            showPreview={!!showPreview}
                            removeContentActionsSection
                            onActionClicked={onKnowActionClicked}
                            showWebappActions={showWebappActions}
                            isDarkTheme={isDarkTheme}
                            referrerScreen={conversionReferrerScreen}
                        />
                    ) : null}
                </ClientOnly>
                {know && showConversionDialog ? (
                    <WebappConversionDialog
                        open={showConversionDialog}
                        title={t('knows/conversionDialogExperimentVariantHeadingFollowVariant')}
                        handleClose={toggleConversionDialog}
                        onButtonClick={handleConversionDialogActionButtonClick}
                        downloadDeepLinkPath={appDeepLinkKnow(know.uuid)}
                        know={know}
                        referrerScreen={conversionReferrerScreen}
                    />
                ) : null}
            </Stack>
        </div>
    )
}

export default KnowSEOContent
